import { CheckCircle2, Clock, Hourglass, XCircle } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'

import { useAccountMembership, useApproveAction, useToday } from '../../app/queries'
import { Button } from '../../components/button'
import { Card, CardTitle } from '../../components/card'
import { Modal } from '../../components/modal'
import { Page, PageHeader } from '../../components/page'
import { ScreenLoading } from '../../components/state'
import { StepUpDialog } from '../../components/step-up'
import { t } from '../../copy'
import { T } from '../../copy/text'
import { cn } from '../../components/cn'

/**
 * 屏 `today` 今天（PRD FR-1 / 研讨会裁决一 / 产品评审 v1）。
 *
 * **北极星落地**：用户打开平台只看这一屏就知道全部——
 * 一句人话总览 + 三张卡（在跑的 / 等你确认的 / 昨夜发生了什么）。
 *
 * ## 产品评审 v1 的七条裁决（docs/today-product-review-v1.md）
 *
 * 1. 总览句**前端拼**：计数是数据，句子是文案——后端那句拼好的中文
 *    在英文模式下破功，文案的唯一来源是 copy 系统（PRD §8）。
 * 2. 「等你确认的」是主角：非空时唯一带强调色的卡；移动端排第一。
 * 3. 每条可点：直达动态中心对应条目（确认动作带证据上下文，不在卡内裸批）。
 * 4. 链接收进卡里，页脚散链删除。
 * 5. 新鲜度：总览行尾挂「数据截至 HH:MM」——15s 轮询已有，不加刷新按钮。
 * 6. 昨夜卡计数仍是全部；failed>0 数字行变琥珀。
 * 7. 本屏新增文案全部双语。
 */
export function TodayScreen() {
  const today = useToday()
  const approve = useApproveAction()
  const account = useAccountMembership()

  /** 正要批的那一条：先问「真要做吗」，确认完才弹二次验证。 */
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [stepUpId, setStepUpId] = useState<string | null>(null)

  if (today.isPending) {
    // 骨架镜像真实结构：真标题（静态文案不闪）+ 副标题条 + 三张卡。
    return <ScreenLoading shape="cards" cols={3} title={<T id="nav.today.page.title" />} />
  }

  if (today.error) {
    return (
      <Page>
        <PageHeader title={<T id="nav.today.page.title" />} className="mb-npc-modal" />
        <div
          role="alert"
          className="rounded-npc-card border border-npc-red-border bg-npc-red-bg p-npc-card"
        >
          <p className="m-0 text-npc-body-sm font-medium text-npc-red-ink"><T id="nav.today.error.title" /></p>
          <button
            type="button"
            onClick={() => void today.refetch()}
            className="mt-[8px] h-[28px] cursor-pointer rounded-npc-tag border-none bg-m-pri px-[13px] font-[inherit] text-[12.5px] text-m-pri-t" /* npc-token-allow: today 错误卡按设计稿原值，token 档位无此值 */
          >
            <T id="error.read_failed.retry" />
          </button>
        </div>
      </Page>
    )
  }

  const data = today.data
  const running = data.running ?? []
  const waiting = data.waiting ?? []
  const runningCount = data.running_count ?? running.length
  const waitingCount = data.waiting_count ?? waiting.length
  // night 字段可能缺失/为 null——真实路径的空值会崩整页（2026-08-29 审计的教训）
  const n = data.night
  const night = {
    succeeded: n?.succeeded ?? 0,
    failed: n?.failed ?? 0,
    rolled_back: n?.rolled_back ?? 0,
    items: n?.items ?? [],
  }

  // 裁决一：总览句前端拼（zh/en 都走 copy）。后端 overview 字段已停用。
  const overview = composeOverview(runningCount, waitingCount, night)

  // **判据是后端给的 capabilities，不是角色名。**「干活的」这一档没有
  // approve_red，按多少次确认都不会通过——给他一个按得下去的按钮，
  // 就是一个一直点、一直失败、还看不出为什么的死循环。
  const canApprove = account.data?.capabilities.includes('approve_red') ?? false
  return (
    <Page>
      <PageHeader
        title={<T id="nav.today.page.title" />}
        subtitle={
          <span className="flex flex-wrap items-baseline gap-x-[10px]">
            <span>{overview}</span>
            <span className="text-npc-meta text-npc-ink-softer">
              <T id="nav.today.freshness" vars={{ time: hhmm(data.generated_at) }} />
            </span>
          </span>
        }
        className="mb-npc-modal"
      />

      {/* 裁决二：等你确认的非空 = 唯一强调卡；移动端 order 排第一 */}
      <div className="grid gap-npc-card-lg lg:grid-cols-3">
        {/* 等你确认的（主角） */}
        <Card
          padding="none"
          data-testid="today-waiting"
          className={cn(
            'max-lg:order-first',
            // Card 自带 border-npc-border，同特异度下后者按 CSS 顺序赢——这里加!important 才真生效
            waiting.length > 0 && 'border-npc-amber-border!',
          )}
        >
          <div
            className={cn(
              'flex items-center justify-between gap-[8px] border-b px-npc-card py-npc-grid-sm',
              waiting.length > 0 ? 'border-npc-amber-border bg-npc-amber-bg' : 'border-npc-border-soft',
            )}
          >
            <CardTitle>
              <span className="flex items-center gap-[6px]">
                <XCircle size={14} className="text-npc-amber-ink" aria-hidden />
                <T id="nav.today.card.waiting" />
              </span>
            </CardTitle>
            <CountChip count={waitingCount} tone={waiting.length > 0 ? 'amber' : 'quiet'} />
          </div>
          <div className="px-npc-card py-npc-grid-sm">
            {waiting.length === 0 ? (
              <p className="m-0 text-npc-body-sm text-npc-ink-soft">
                <T id="nav.today.empty.waiting" />
              </p>
            ) : (
              <ul className="m-0 list-none p-0">
                {waiting.map((item: TodayItem) => (
                  <TodayRow
                    key={item.id ?? item.title}
                    item={item}
                    kind="waiting"
                    canApprove={canApprove}
                    onApprove={(actionId) => {
                      approve.reset()
                      setPendingId(actionId)
                    }}
                    approving={approve.isPending && approve.variables?.actionId === item.id}
                    approveError={
                      approve.error && approve.variables?.actionId === item.id
                        ? approve.error.message
                        : undefined
                    }
                  />
                ))}
              </ul>
            )}
          </div>
          <CardFooterLink
            to={waiting.length > 0 ? '/activity?view=action' : '/activity'}
            labelId="nav.today.link.inbox"
          />
        </Card>

        {/* 在跑的 */}
        <Card padding="none" data-testid="today-running">
          <div className="flex items-center justify-between gap-[8px] border-b border-npc-border-soft px-npc-card py-npc-grid-sm">
            <CardTitle>
              <span className="flex items-center gap-[6px]">
                <Clock size={14} className="text-npc-ink-softer" aria-hidden />
                <T id="nav.today.card.running" />
              </span>
            </CardTitle>
            <CountChip count={runningCount} tone="quiet" />
          </div>
          <div className="px-npc-card py-npc-grid-sm">
            {running.length === 0 ? (
              <p className="m-0 text-npc-body-sm text-npc-ink-soft">
                <T id="nav.today.empty.running" />
              </p>
            ) : (
              <ul className="m-0 list-none p-0">
                {running.map((item: TodayItem) => (
                  <TodayRow key={item.id ?? item.title} item={item} kind="running" />
                ))}
              </ul>
            )}
          </div>
          <CardFooterLink to="/timeline" labelId="nav.today.link.all_activity" />
        </Card>

        {/* 昨夜发生了什么（裁决六：failed>0 数字行变琥珀） */}
        <Card padding="none" data-testid="today-night">
          <div className="border-b border-npc-border-soft px-npc-card py-npc-grid-sm">
            <CardTitle>
              <span className="flex items-center gap-[6px]">
                <CheckCircle2 size={14} className="text-npc-green-deep" aria-hidden />
                <T id="nav.today.card.night" />
              </span>
            </CardTitle>
          </div>
          <div className="px-npc-card py-npc-grid-sm">
            <p
              className={cn(
                'm-0 text-npc-body-sm text-npc-ink-medium',
                night.failed > 0 && 'text-npc-amber-ink',
              )}
            >
              <T
                id="nav.today.card.night.body"
                vars={{
                  succeeded: String(night.succeeded),
                  failed: String(night.failed),
                  rolled: String(night.rolled_back),
                }}
              />
            </p>
            {/* L2 闭环：昨夜明细——后端已按「失败在前」排好，这里照单渲染。
                每条可点直达动态中心（与另两卡同款交互）。 */}
            {night.items.length > 0 ? (
              <ul className="m-0 mt-[6px] list-none p-0">
                {night.items.map((item) => (
                  <li key={item.id} className="border-b border-npc-border-soft last:border-b-0">
                    <Link
                      to={(item.id ? `/activity?activity_id=${item.id}` : '/timeline') as string}
                      data-testid="today-item-night"
                      className="flex items-start gap-[8px] rounded-npc-tag px-[6px] py-[7px] transition-colors hover:bg-npc-surface"
                    >
                      {item.status === 'succeeded' ? (
                        <CheckCircle2 size={13} className="mt-[2px] shrink-0 text-npc-green-deep" aria-hidden />
                      ) : (
                        <XCircle size={13} className="mt-[2px] shrink-0 text-npc-amber-ink" aria-hidden />
                      )}
                      <span className="min-w-0 flex-1">
                        <span
                          className={cn(
                            'block truncate text-npc-body-sm',
                            item.status === 'succeeded' ? 'text-npc-ink-strong' : 'text-npc-amber-ink',
                          )}
                        >
                          {item.title}
                        </span>
                        <span className="block text-npc-meta text-npc-ink-softer">{item.happened_ago}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <CardFooterLink to="/timeline" labelId="nav.today.link.night" />
        </Card>
      </div>
      <Modal
        open={pendingId !== null}
        onOpenChange={(next) => {
          if (!next) setPendingId(null)
        }}
        title={t('timeline.approve.confirm.title')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPendingId(null)}>
              <T id="account.stepup.cancel" />
            </Button>
            <Button
              variant="danger"
              data-testid="today-approve-confirm"
              onClick={() => {
                setStepUpId(pendingId)
                setPendingId(null)
              }}
            >
              <T id="timeline.action.approve" />
            </Button>
          </>
        }
      >
        <p className="m-0 text-npc-body-sm leading-[1.7] text-npc-ink-soft">
          <T id="timeline.approve.confirm.body" />
        </p>
      </Modal>

      <StepUpDialog
        open={stepUpId !== null}
        intent="approve_red_action"
        resource={stepUpId ?? ''}
        description={t('timeline.approve.confirm.body')}
        onCancel={() => setStepUpId(null)}
        onVerified={(token) => {
          const actionId = stepUpId
          setStepUpId(null)
          if (!actionId) return
          approve.mutate({ actionId, stepUpToken: token })
        }}
      />
    </Page>
  )
}

/** 裁决一：总览句按计数拼。四种成分：在跑 / 等确认 / 昨夜做成的 / 没成的。 */
function composeOverview(running: number, waiting: number, night: { succeeded: number; failed: number; rolled_back: number }): string {
  if (running === 0 && waiting === 0 && night.succeeded === 0 && night.failed === 0) {
    return t('nav.today.overview.all')
  }
  const parts: string[] = []
  if (running > 0) parts.push(t('nav.today.overview.running', { count: running }))
  if (waiting > 0) parts.push(t('nav.today.overview.waiting', { count: waiting }))
  if (night.succeeded > 0) {
    parts.push(t('nav.today.overview.night', { succeeded: night.succeeded }))
  }
  if (night.failed > 0) parts.push(t('nav.today.overview.night.failed', { failed: night.failed }))
  return parts.join(' · ')
}

/** 卡头计数 chip（裁决六之外的小件：扫一眼就知道几件）。 */
function CountChip({ count, tone }: { count: number; tone: 'amber' | 'quiet' }) {
  return (
    <span
      data-testid="today-count-chip"
      className={cn(
        'shrink-0 rounded-npc-chip px-[8px] py-[1px] text-npc-meta font-semibold',
        tone === 'amber' ? 'bg-npc-amber-bg text-npc-amber-ink' : 'bg-npc-surface text-npc-ink-softer',
      )}
    >
      <T id="nav.today.count.items" vars={{ count }} />
    </span>
  )
}

/** 裁决三：条目可点——直达动态中心对应条目。 */
function TodayRow({
  item,
  kind,
  canApprove = false,
  onApprove,
  approving = false,
  approveError,
}: {
  item: TodayItem
  kind: 'running' | 'waiting'
  /** 这个身份能不能批红级（`approve_red`）。**由后端 capabilities 说了算。** */
  canApprove?: boolean
  onApprove?: (actionId: string) => void
  approving?: boolean
  approveError?: string
}) {
  const target = item.id ? `/activity?activity_id=${item.id}` : '/activity'
  return (
    // `data-action-id` 让用例锚在**具体哪一条**上。夹具里等确认的不止一条，
    // 而 `getAllByTestId(...)[0]` 那种写法会在夹具变一次顺序时静静指向另一条。
    <li className="border-b border-npc-border-soft last:border-b-0" data-testid="today-row" data-action-id={item.id}>
      <Link
        to={target}
        data-testid={`today-item-${kind}`}
        className="flex items-start gap-[8px] rounded-npc-tag px-[6px] py-[8px] text-npc-ink-strong transition-colors hover:bg-npc-surface"
      >
        {kind === 'waiting' ? (
          <span className="mt-[7px] size-[6px] shrink-0 rounded-full bg-npc-amber" aria-hidden />
        ) : (
          <Hourglass size={13} className="mt-[3px] shrink-0 text-npc-amber-ink" aria-hidden />
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-npc-body-sm">{item.title}</span>
          <span className="block text-npc-meta text-npc-ink-softer">
            {kind === 'waiting' ? item.waiting_ago : item.started_ago}
          </span>
        </span>
      </Link>

      {/* ==================================================================
          「等你确认的」这张卡在这一步之前**只能看不能确认**：用户看到一条
          停着的红级动作，没有任何办法让它继续。按钮就摆在这条下面，
          不用他先想到去翻时间线。

          **它在 `<Link>` 外面**，不是里面：`<button>` 套在 `<a>` 里既是
          不合法的 HTML，点下去还会顺带跳走——他想批一件事，结果换了一屏。
          ================================================================== */}
      {kind === 'waiting' && item.id ? (
        <div className="px-[6px] pb-[8px]">
          {canApprove ? (
            <button
              type="button"
              data-testid="today-approve"
              disabled={approving || !onApprove}
              onClick={() => onApprove?.(item.id!)}
              className="cursor-pointer border-none bg-transparent p-0 font-[inherit] text-npc-meta text-npc-amber-ink disabled:cursor-not-allowed"
            >
              <T id="timeline.action.approve" />
            </button>
          ) : (
            <p data-testid="today-approve-forbidden" className="m-0 text-npc-meta text-npc-ink-softer">
              <T id="timeline.approve.forbidden" />
            </p>
          )}
          {approveError ? (
            <p role="alert" data-testid="today-approve-error" className="m-0 mt-[4px] text-npc-meta text-npc-red-ink">
              {approveError}
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

/** 裁决四：链接收进卡底——卡与去向一一对应。 */
function CardFooterLink({ to, labelId }: { to: string; labelId: 'nav.today.link.all_activity' | 'nav.today.link.inbox' | 'nav.today.link.night' }) {
  return (
    <div className="border-t border-npc-border-soft px-npc-card py-[8px]">
      <Link
        to={to}
        className="text-npc-caption text-npc-green underline decoration-npc-border-soft underline-offset-2 hover:text-npc-ink-strong"
      >
        <T id={labelId} />
      </Link>
    </div>
  )
}

/** 今天屏的一条（在跑或待确认）。 */
type TodayItem = {
  id?: string
  title?: string
  status?: string
  started_ago?: string
  waiting_ago?: string
}

/** ISO 时间 → HH:MM。给「数据截至」用，两语言都不带时区词。 */
function hhmm(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}
